import type { Ref } from "react";
import { forwardRef, memo } from "react";
import clsx from "clsx";

import { Icon } from "../icon";
import { Typography } from "../typography";

import type { IDropdownItemProps } from "./types";
import rootClasses from "./styles.module.scss";

import { Flag } from "@components/atoms/flag";

export const DropdownItem = memo(
  forwardRef(function DropdownItem(
    props: IDropdownItemProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const {
      isSelected,
      image,
      iconName,
      flag,
      children,
      className,
      disabled,
      text,
      description,
      imageContainerClassName,
      key,
      ...rest
    } = props;

    const iconHasBorder = !!description;

    return (
      <div
        ref={ref}
        className={clsx([
          rootClasses.box,
          isSelected && rootClasses.selected,
          className,
        ])}
        data-disabled={disabled}
        {...rest}
        key={key}
      >
        {(iconName || image || flag) && (
          <div
            className={clsx([
              rootClasses["icon-container"],
              iconHasBorder && rootClasses["icon-border"],
            ])}
          >
            {iconName && (
              <Icon
                name={iconName}
                fontSize={"fs-18"}
                color={disabled === true ? "icon-disabled-300" : "icon-sub-600"}
              />
            )}
            {flag && <Flag flag={flag} />}
            {image && (
              <div
                className={clsx([
                  rootClasses["image-container"],
                  imageContainerClassName,
                  disabled && rootClasses["image-disabled"],
                ])}
              >
                {image}
              </div>
            )}
          </div>
        )}
        {(text || description) && (
          <div
            className={clsx(
              ["d-flex-column gap-4 justify-content-center"],
              rootClasses.grow,
            )}
          >
            {text && (
              <Typography
                fontType={"paragraph-small"}
                themeColor={disabled ? "text-disabled-300" : "text-strong-900"}
                className={rootClasses.autoAlignment}
              >
                {text}
              </Typography>
            )}
            {description && (
              <Typography
                fontType={"paragraph-x-small"}
                themeColor={
                  disabled === true ? "icon-disabled-300" : "text-sub-600"
                }
              >
                {description}
              </Typography>
            )}
          </div>
        )}
        {isSelected && (
          <Icon name={"CheckLine"} fontSize={"fs-20"} color={"icon-sub-600"} />
        )}
        {children}
      </div>
    );
  }),
);

DropdownItem.displayName = "DropdownItem";
